<template>
    <div class="text-nowrap">
        <Spinner v-if="Object.keys(comparingTrainings).length === 0"/>
        <template v-else>
            <select v-model="selectedTrainingId" class="select-athlete mr-2">
                <option value="" disabled="disabled" selected>Select video</option>

                <optgroup :label="'My Videos, Last 30s, ' + strokesPerMinute + ' SPM'">
                    <option v-for="comparingTraining in getMyTrainins"
                            :value="comparingTraining.id"
                            :key="'MyTrainings'+comparingTraining.id"
                            :disabled="comparingTraining.id === training.id"
                    >
                        {{getParticipantName(comparingTraining)}},
                        id: {{comparingTraining.id}},
                        Score: {{getAvgScore(comparingTraining)}}%,
                        {{formatDateTime(comparingTraining.createdAt)}}{{comparingTraining.id === training.id ? ', Current video' : ''}}
                    </option>
                    <option v-if="isEmptyTrainings(getMyTrainins)" value="" disabled>No trainings with {{strokesPerMinute}} SPM to compare</option>
                </optgroup>

                <optgroup :label="publicGroupLabelLine1"></optgroup>
                <optgroup :label="publicGroupLabelLine2">
                    <option v-for="comparingTraining in comparingTrainings['Public']"
                            :value="comparingTraining.id"
                            :key="'Public'+comparingTraining.id"
                            :disabled="comparingTraining.id === training.id"
                    >
                        id: {{comparingTraining.id}},
                        Score: {{getAvgScore(comparingTraining)}}%,
                        {{formatDateTime(comparingTraining.createdAt)}}{{comparingTraining.id === training.id ? ', Current video' : ''}}
                    </option>
                    <option v-if="isEmptyTrainings(comparingTrainings['Public'])" value="" disabled>No good videos to compare</option>
                </optgroup>

            </select>
            <CBadge
                class="athlete-badge"
                color="blue"
                shape="pill"
                v-c-tooltip="'To feature your video in TOP videos, set it to public. Inclusion is score-based.<br>To remove it from TOP videos, set it to private.'"
            ><strong style="font-size: 12px;">!</strong></CBadge>
        </template>
    </div>
</template>

<script>
import Spinner from "@/components/Spinner";
import {apolloClient} from '@/plugins/apollo'
import gql from 'graphql-tag'
import {getTrainingQueryBody} from '@/scripts/queries'
import moment from "moment/moment";
import {getDateFormat} from '@/scripts/utils';

const topQty = 5,
    NONAME = 'NoName';

export default {
    components: {
        Spinner
    },
    props: {
        training: Object,
    },
    data() {
        return {
            comparingTrainings: {},
            selectedTrainingId: '',
            topQty: topQty,
        }
    },
    methods: {
        async loadSimilarTrainings(training) {
            let fetchRes = await apolloClient.query({
                query: gql`
                    {
                        Public: ${this.queryTrainings('Public', training)}
                        MyTrainings: ${this.queryTrainings('MyTrainings', training)}
                    }
                `
            });
            if (fetchRes.data) {
                let data = fetchRes.data;
                data['MyTrainings'] = this.sortTrainings(data['MyTrainings'])
                this.comparingTrainings = data;
            } else {
                console.error('Unexpected response', fetchRes);
                this.comparingTrainings = {};
            }
        },

        /**
         *
         * @param {Array} trainings
         */
        isEmptyTrainings(trainings) {
            if (!Array.isArray(trainings) || trainings.length < 1) {
                return true;
            }
            if (trainings?.length === 1 && trainings[0].id === this.training.id) {
                return true;
            }
            return false;
        },

        queryTrainings(listNames, training) {
            const ergType = training.equipmentType === 'ERG' ? `ergType: "${training.ergType}",` : '';
            const firstN = listNames === 'Public' ? `firstN: ${this.topQty},` : `firstN: 30,`;

            return `trainings (
                        listNames: [${listNames}],
                        ${firstN}
                        equipmentType: "${training.equipmentType.toLowerCase().charAt(0).toUpperCase() + training.equipmentType.toLowerCase().slice(1)}",
                        strokes: [${this.spmRounded},${this.spmRounded}],
                        orderBy: "avg_score",
                        ${ergType}
                        trainingProfileName: "${training.trainingProfile?.name}",
                        ${training.equipmentType === 'BOAT' ? 'boatTypes: 1,' : ''}
                        ${listNames === 'MyTrainings' ? 'processedVideosOnly: true,' : 'minScore: 70,'}
                        createdAfter: "2021-08-24T06:41:00Z"
                    ) { ${ getTrainingQueryBody() } }`
        },
        formatDateTime(dateStr) {
            return moment.utc(dateStr).local().format(`${this.dateFormat}`)
        },
        getAvgScore(training) {
            return training.videos[0].videoProcessingResult.avgScore;
        },
        getParticipantName(training) {
            if (training.participant1?.username) {
                return training.participant1?.username;
            }

            if (training.participant1CustomName) {
                return training.participant1CustomName;
            }
            return NONAME;
        },
        sortTrainings(trainings) {
            return trainings.sort((a, b) => {
                const aTime = moment(a.createdAt).startOf('day').unix(),
                    bTime = moment(b.createdAt).startOf('day').unix(),
                    aScore = this.getAvgScore(a),
                    bScore = this.getAvgScore(b),
                    aName = this.getParticipantName(a) === NONAME ? 'zzz' : this.getParticipantName(a),
                    bName = this.getParticipantName(b) === NONAME ? 'zzz' : this.getParticipantName(b);

                if (aTime > bTime) {
                    return -1;
                } else if (aTime < bTime) {
                    return 1;
                } else if (aScore > bScore) {
                    return -1;
                } else if (aScore < bScore) {
                    return 1;
                } else {
                    return 0;
                }
            });
        }
    },
    computed: {
        spmRounded() {
            return this.training.videos[0].videoProcessingResult?.spmRounded * 1;
        },
        publicTrainingName() {
            return this.training.equipmentType === 'ERG'
                ? `Erg: ${this.training.ergType}`
                : `Boat: ${this.training.boatType.slice(4).toLowerCase()
                    .replace('_p', '+').replace('_m', '-')}`;
        },
        videosNotFound() {
            return this.comparingTrainings['Public'] && this.comparingTrainings['MyTrainings']
                && this.comparingTrainings['Public'].length === 0
                && this.comparingTrainings['MyTrainings'].length === 0;
        },
        strokesPerMinute() {
            return this.training.videos[0]?.videoProcessingResult?.spmRounded * 1;
        },
        publicGroupLabelLine1() {
            return `Public Videos, Top ${this.topQty} Score, ${this.strokesPerMinute} SPM`;
        },
        publicGroupLabelLine2() {
            return (this.training.equipmentType === 'ERG'
                ? `Erg: ${this.training.ergType}`
                : `Boat: ${this.training.boatType.slice(4).toLowerCase().replace('_p', '+').replace('_m', '-')}`)
                + `, ${this.training.trainingProfile.name}`;
        },
        dateFormat() {
            return getDateFormat();
        },
        getMyTrainins() {
            return Array.isArray(this.comparingTrainings['MyTrainings'])
                ? this.comparingTrainings['MyTrainings']
                : [];
        }
    },
    created() {
        this.loadSimilarTrainings(this.training);
    },
    watch: {
        selectedTrainingId(trainingId) {
            let training = null;
            Object.keys(this.comparingTrainings).forEach((key) => {
                if (!Array.isArray(this.comparingTrainings[key])) {
                    return;
                }

                let selectedTraining = this.comparingTrainings[key].find((training) => {
                    return training.id === trainingId;
                });
                if (selectedTraining) {
                    training = selectedTraining;
                }
            });

            this.$emit('selectTraining', training);
        }
    }
}
</script>

<style lang="scss">
.select-athlete {
    height: 35px;
    border-radius: 0.25rem;
    color: #4f5d73;
    border-color: #cfd4d8;
    outline: none;
    max-width: 320px;
}
@media (max-width: 700px) {
    .select-athlete {
        max-width: 240px;
    }
}

.badge-blue {
    color: #fff;
    background-color: #39f;
}
</style>